import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, Sparkles, CheckCircle2, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { api } from "@/lib/api";

const programs = [
  {
    icon: Heart,
    title: "1:1 Coaching",
    desc: "Private, customized coaching for clarity in life, work, and family. Sessions are 60 minutes, twice a month.",
    bullets: [
      "Personalized growth plan",
      "Voice + text access between sessions",
      "Tools and prompts tailored to you",
    ],
  },
  {
    icon: Users,
    title: "Mental & Emotional Release",
    desc: "A powerful, results-oriented technique that helps you identify and release limiting beliefs, old patterns holding you back, and emotional blocks from previous trauma or experiences. Most people notice a shift in a single session — whether you're working through anxiety, self-sabotage, grief, fear, or a recurring pattern that no longer serves you. MER® offers a gentle, non-clinical path to genuine breakthrough.",
    bullets: [
      "Six 1:1 sessions per month",
      "One hour each, private and virtual",
      "MER® certified practitioner",
    ],
  },
  {
    icon: Sparkles,
    title: "Cornerstone Advantage",
    desc: "The complete coaching system for productivity coaches, team leaders, and operating principals — three packages to fit where you are: Basic, Cornerstone Plus, and Premier. Every tier includes your own AI coaching assistant available 24/7, full community access, exclusive trainings, our digital asset library, and the live implementation lab.",
    bullets: [
      "Three packages: Basic · Cornerstone Plus · Premier",
      "24/7 AI coaching assistant + full community access",
      "Exclusive trainings, digital asset library, live implementation lab",
    ],
    href: "/leadership-coaching",
    ctaLabel: "Open the Cornerstone guide",
  },
];

export default function Coaching() {
  return (
    <div data-testid="coaching-page">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-16 grid md:grid-cols-12 gap-12 items-end">
        <div className="md:col-span-7">
          <span className="text-[11px] uppercase tracking-[0.3em] text-primary">Coaching</span>
          <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl mt-4 leading-[1.02]">
            Coaching that meets
            <br />
            you where <span className="italic text-primary">you actually are.</span>
          </h1>
        </div>
        <p className="md:col-span-4 md:col-start-9 text-lg text-foreground/80 leading-relaxed">
          Whether you're rebuilding after a hard season or stepping into a
          bigger one — coaching with Tammy is a place to slow down, think
          clearly, and act with intention.
        </p>
      </section>

      {/* Programs */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:pt-24">
        <div className="grid lg:grid-cols-3 gap-6">
          {programs.map((p, i) => (
            <div
              key={p.title}
              className={`bg-card border border-border rounded-2xl p-8 card-lift flex flex-col ${i === 1 ? "lg:scale-[1.02] lg:border-primary/60" : ""}`}
              data-testid={`coaching-program-${i}`}
            >
              <p.icon className="text-primary" size={28} strokeWidth={1.5} />
              <h3 className="font-serif text-3xl mt-5">{p.title}</h3>
              <p className="text-sm text-foreground/70 mt-3 leading-relaxed">{p.desc}</p>
              <ul className="mt-6 space-y-2 text-sm text-foreground/80">
                {p.bullets.map((b) => (
                  <li key={b} className="flex gap-2">
                    <CheckCircle2 className="text-primary mt-0.5 shrink-0" size={16} strokeWidth={1.6} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <Link to={p.href || "/contact"} className="mt-8" data-testid={`coaching-program-cta-${i}`}>
                <Button variant="outline" className="w-full rounded-full border-border h-11">
                  {p.ctaLabel || "Start a conversation"}
                </Button>
              </Link>
              {i === 0 && (
                <OrientationDialog />
              )}
            </div>
          ))}
        </div>
      </section>

      {/* For producing agents — performance-based offer */}
      <section className="bg-[#0F1E33] text-[#F5F8FB]" data-testid="producer-coaching-strip">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <span className="inline-block text-[11px] uppercase tracking-[0.3em] text-[#93C5FD] mb-4">
              For producing agents
            </span>
            <h2 className="font-serif text-4xl md:text-5xl leading-tight">
              Coaching you only pay for
              <br />
              <span className="italic text-[#93C5FD]">when you win.</span>
            </h2>
            <p className="mt-6 text-lg opacity-85 leading-relaxed max-w-xl">
              Already producing? Tammy offers a performance-based coaching
              engagement — no retainer, no upfront fees. The coaching fee is
              only paid once your production crosses an agreed threshold.
            </p>
          </div>
          <div className="md:col-span-5 md:text-right">
            <Link to="/producer-coaching" data-testid="coaching-producer-cta">
              <Button className="rounded-full bg-[#93C5FD] hover:bg-[#BAE6FD] text-[#0F1E33] btn-lift h-12 px-8 text-base">
                Learn more <ArrowRight className="ml-2" size={18} />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 py-28 text-center">
        <h2 className="font-serif text-4xl md:text-5xl leading-tight">
          Ready to begin?
        </h2>
        <p className="text-lg text-foreground/75 mt-6">
          Book a discovery call — it's free, and it's the only way to know
          if coaching with Tammy is right for you.
        </p>
        <Link to="/booking" data-testid="coaching-cta-book">
          <Button className="mt-10 rounded-full bg-primary hover:bg-primary/90 btn-lift h-12 px-8 text-base">
            Book a Discovery Call <ArrowRight className="ml-2" size={18} />
          </Button>
        </Link>
      </section>
    </div>
  );
}


/* ----- Orientation signup dialog: user provides name+email, Zoom invite is emailed ----- */
function OrientationDialog() {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/orientation/signup", {
        name: name.trim() || undefined,
        email: email.trim(),
      });
      toast.success("Check your inbox — your Zoom invite is on its way.");
      setOpen(false);
      setName("");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Something went wrong — please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="orientation-open-btn"
          className="mt-3 w-full rounded-full bg-primary hover:bg-primary/90 h-11 btn-lift"
        >
          <CalendarClock size={16} className="mr-2" />
          Sign up for Orientation
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="orientation-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">
            MREA Business Coaching Orientation
          </DialogTitle>
          <DialogDescription>
            Weekly Zoom · Every Tuesday · 11:30 AM Eastern. Enter your email
            and we'll send you the recurring invite right away.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 pt-2">
          <div>
            <Label
              htmlFor="orientation-name"
              className="text-xs uppercase tracking-[0.18em] text-foreground/70"
            >
              Name (optional)
            </Label>
            <Input
              id="orientation-name"
              data-testid="orientation-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className="rounded-xl h-11 mt-2"
            />
          </div>
          <div>
            <Label
              htmlFor="orientation-email"
              className="text-xs uppercase tracking-[0.18em] text-foreground/70"
            >
              Email
            </Label>
            <Input
              id="orientation-email"
              type="email"
              data-testid="orientation-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="rounded-xl h-11 mt-2"
              required
            />
          </div>
          <DialogFooter className="pt-2">
            <Button
              type="submit"
              disabled={submitting}
              data-testid="orientation-submit-btn"
              className="w-full rounded-full bg-primary hover:bg-primary/90 h-11"
            >
              {submitting ? "Sending..." : "Email me the invite"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
